import React, { useEffect, useRef, useState } from 'react';
import { SAFE_DEFAULT_COLOR } from '../../../_constants';
import Portal from '../../../components/Portal';
import ColorPickerModal from '../../../components/Modal/ColorPickerModal';
import { useSetAuth } from '../../../utils/useSetAuth';
import { InputTable } from '../../../assets/styles/InputTable';
import { useTranslation } from 'react-i18next';
import CheckBoxCircle from '../../../components/button/CheckBoxCircle';
import styled from 'styled-components';
import { BtnBlue, BtnGreen, BtnRed } from '../../../components/Button';
import ClickableContainer from '../../s_cctv/S_cctv1/RealtimeCCTV/ClickableContainer';
import TableLamp from '../../../components/TableLamp';
import JobTypeModal from '../../sm_setting/setting12/JobTypeModal';
import JobtypeChip from '../../sm_setting/setting12/JobtypeChip';
import { findJobTypeName } from '../../../hooks/useJobType';
import GroupChkModal from '../../../components/Modal/GroupChkModal';
import { useRecoilValue } from 'recoil';
import { userState } from '../../../atoms';
import { useQuery } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPost } from '../../../services/_common';
import DeleteModal from '../../../components/Modal/DeleteModal';
import { v4 } from 'uuid';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import { applyBorderStyle } from '../../../utils/applyBorderStyle';
import { trimArray } from '../../../utils/trimArray';
import { IModal } from 'customTypes';
import { logPost } from '../../../services/log';

const ButtonsWrapper = styled.div`
  border-top: 1px solid var(--outline);
  display: flex;
  gap: 0.5rem;
  justify-content: space-between;
  padding: 0.5rem;
  > div {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }
  button {
    height: 2.5rem;
    font-size: 0.875rem;
  }
  .countWrapper {
    padding: 0 0.25rem;
  }
  .countName {
    font-size: 0.875rem;
    color: var(--text-secondary);
  }
  .countNumber {
    font-size: 0.875rem;
    color: var(--primary);
    background-color: var(--tonal-blue);
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    margin-left: 0.5rem;
  }
  .seperator {
    height: 1rem;
    border-left: 1px solid var(--outline);
    margin: 0 0.25rem;
  }
`;

const Root = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: fit-content;
  overflow: hidden;
  justify-content: flex-start;

  .colorSelect {
    color: var(--text-primary);
  }

  .header {
    padding: 0.75rem;
    height: 3rem;
    display: flex;
    align-items: center;
    font-size: 0.875rem;
    color: var(--text-secondary);
    display: flex;
    gap: 0.5rem;
    justify-content: center;
    .title {
      flex-shrink: 0;
      width: fit-content;
      font-weight: 600;
      margin-right: 0.5rem;
    }
    input {
      width: 12rem;
    }
  }

  .jobtype-chip-container {
    height: 100%;
    display: flex;
    align-items: stretch;
    gap: 0.5rem;
    .button {
      flex-shrink: 0;
      user-select: none;
      cursor: pointer;
      height: 2rem;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 0.25rem;
      padding: 0 0.5rem;
      margin: auto 0;
      .material-symbols-rounded {
        font-variation-settings: 'FILL' 1, 'wght' 400;
        font-size: 1.5rem;
      }
    }
    .default-button {
      background-color: var(--tonal);
      color: var(--filled-slate-deep);
      &:hover {
        outline: 0.25rem solid var(--filled-slate);
        &:active {
          transform: scale(0.95);
        }
      }
    }
    .add-button {
      background-color: var(--filled-slate-deep);
      color: var(--board);
      &:hover {
        outline: 0.25rem solid var(--tonal-deep);
        &:active {
          transform: scale(0.95);
        }
      }
    }
    .delete-button {
      background-color: var(--tonal-red);
      color: var(--filled-red);
      &:hover {
        outline: 0.25rem solid var(--filled-red);
        &:active {
          transform: scale(0.95);
        }
      }
    }
    .scroll-container {
      padding-top: 0.75rem;
      padding-right: 0.5rem;
      flex-grow: 1;
      display: flex;
      gap: 0.5rem;
      align-items: center;
      overflow-x: scroll;
      overflow-y: hidden;
      white-space: nowrap;
    }
    .checklist-container {
      flex-grow: 1;
      display: flex;
      align-items: center;
    }
    .checklist {
      margin: 0.5rem 0;
      height: 2rem;
      border-radius: 0.25rem;
      display: flex;
      align-items: center;
      gap: 0.25rem;
      background-color: var(--tonal-green);
      padding-left: 0.375rem;
      padding-right: 1rem;
      .material-symbols-rounded {
        font-size: 1.375rem;
        color: var(--filled-green);
        display: flex;
        align-items: center;
        justify-content: center;
        height: 1.75rem;
        width: 1.75rem;
        border-radius: 0.25rem;
      }
      .description {
        font-size: 0.875rem;
        color: var(--filled-green-deep);
      }
    }
  }
`;

interface IId {
  id: string;
}

interface ISchedule extends IId {
  csCd: string;
  csName: string;
  backColor: string;
  fontColor: string;
  userId: string[];
  useYn: 'Y' | 'N';
}

interface IUser {
  key: string;
  name: string;
}
interface IUserPart {
  grCd: string;
  grName: string;
  subList: IUser[];
}

const CalendarItem = () => {
  const { t } = useTranslation();
  const { auth } = useSetAuth(); // 사용자 권한값 훅
  const userInfo = useRecoilValue(userState);
  const size = useOutletContext<any>();
  const [tableState, setTableState] = useState<ISchedule[]>([]);
  const [userPartList, setUserPartList] = useState<IUserPart[]>([]);
  const [isSaveClicked, setIsSaveClicked] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<IModal | any>({ status: false, type: '', title: '' });
  const [color, setColor] = useState('#000');

  const [jobtypeIdx, setJobtypeIdx] = useState<string>('');

  const [flattenSubList, setFlattenSubList] = useState<any[]>([]);

  const scrollContainerRef = useRef<HTMLInputElement>(null);

  // id 바인딩 함수
  const newIdBindFn = (arr: ISchedule[]) => arr.map((el: ISchedule) => ({ ...el, id: v4() }));

  const { data: scheduleApi, isLoading, isError, refetch } = useQuery(['/schedule/set'], () => apiGet({ path: '/schedule/set', req: { cCd: userInfo?.hCd } }));
  // const userPartApi = { data: { data: [] } };
  const { data: userPartApi } = useQuery(['/user/company/part'], () => apiGet({ path: '/user/company/part', req: { hCd: userInfo?.hCd } }));

  useEffect(() => {
    scheduleApi && setTableState(newIdBindFn(scheduleApi.data.data));
  }, [scheduleApi]);

  useEffect(() => {
    userPartApi &&
      setUserPartList(
        userPartApi?.data?.data?.map((el: any) => ({
          grCd: v4(),
          grName: el.partName,
          subList: el.users.map((el2: any) => ({ key: el2.userId, name: el2.userName })),
        }))
      );
  }, [userPartApi]);

  const flattenSubLists = (data: any): any => {
    return data
      ? data?.reduce((acc: any, group: any) => {
          return acc.concat(group.users);
        }, [])
      : [];
  };
  const flattenList = userPartApi && flattenSubLists(userPartApi?.data?.data).map((el2: any) => ({ key: el2.userId, name: el2.userName }));

  // 컬러 picker 열기
  const onClickOpenColorPicker = (el: string, index?: number, colorType?: string) => {
    if (index !== undefined && colorType !== undefined) {
      // setColor(el);
      setOpenModal((prev: any) => ({
        ...prev,
        status: true,
        type: 'object',
        title: 'colorPicker',
        tit: colorType === 'bg' ? '배경' : '글자',
        colorType,
        state: tableState,
        setState: tableState,
        color: el,
        setColorState: setColor,
        el: tableState[index],
        index,
      }));
    }
    /*
    else {
      setOpenModal((prev: any) => ({ ...prev, status: true, type: 'array', title: 'colorPicker', state: data, setState: setData, color, setColorState: setColor, index }));
    }
    */
  };

  // 인풋 값 변경
  const onChangeInputValue = (e: any, idx: number) => {
    setTableState((prev: ISchedule[]) => prev.map((item: ISchedule, i: number) => (i === idx ? { ...item, [e.target.name]: e.target.value } : item)));
  };

  // 사용 유무
  const onClickUseYn = (val: 'Y' | 'N', idx: number) => {
    const newUseYn = val === 'Y' ? 'N' : 'Y';
    setTableState((prev: ISchedule[]) => prev.map((item: ISchedule, i: number) => (i === idx ? { ...item, useYn: newUseYn } : item)));
  };

  // row 추가
  const onClickAddTableRow = () => {
    const newObj: ISchedule = {
      id: v4(),
      csCd: '',
      csName: '',
      backColor: '',
      fontColor: '',
      useYn: 'Y',
      userId: [],
    };
    setTableState([...tableState, newObj]);
  };

  // 저장
  const onClickSave = () => {
    setIsSaveClicked(true);

    const newRowRequired: string[] = ['csName', 'backColor', 'fontColor', 'userId'];

    const emptyCheck = tableState.filter((el: any) => {
      const check = newRowRequired.find((el2: string) => el[el2] === '' || (el2 === 'userId' && el[el2][0] === ''));
      return check;
    });

    if (emptyCheck.length > 0) {
      toast.warning(t('필수입력값을 모두 입력하세요'));
    } else {
      saveScheduleAPI();
    }
  };

  const saveScheduleAPI = async () => {
    try {
      const req = {
        scheduleSet: trimArray(
          tableState.map((item: ISchedule) => ({
            cCd: userInfo.hCd,
            csCd: item.csCd || '',
            csName: item.csName,
            backColor: item.backColor,
            fontColor: item.fontColor,
            userId: item.userId.join('|'),
            useYn: item.useYn,
          }))
        ),
      };
      const res = await apiPost({ path: '/schedule/set', req });
      const { data: scheduleListData, message, statusCode } = res.data;

      if (statusCode === 200) {
        refetch();
        toast.success(t(message));
        await logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '환경 설정 > 일정 설정',
          action: '저장',
          etc: `일정 설정 저장`,
        });
      }
    } catch (err) {
      console.error(err);
      throw new Error('error');
    }
  };

  // row 제거 및 삭제
  const onClickDelete = (el: ISchedule, idx: number) => {
    // 새로 추가한 로우 제거 클릭 시
    if (el.csCd === '') {
      const newMap: ISchedule[] = [...tableState];
      newMap.splice(idx, 1);
      setTableState(newMap);
    } else {
      setOpenModal((prev: IModal) => ({ ...prev, type: 'delete', status: true, state: tableState, setState: setTableState, api: deleteScheduleAPI, el, index: idx }));
    }
  };

  // 삭제 API
  const deleteScheduleAPI = async (el: ISchedule) => {
    try {
      const req = {
        cCd: userInfo.hCd,
        csCd: el.csCd,
      };
      const res = await apiDelete({ path: '/schedule/set', req });
      const { data: scheduleListData, statusCode } = res.data;
      if (statusCode === 200) {
        // setTableState(newIdBindFn(scheduleListData));
        refetch();
        toast.success(t('삭제되었습니다.'));
        await logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '일정 관리 > 일정 항목 관리',
          action: `일정 항목 삭제`,
          etc: `일정 항목 명 : ${el.csName}`,
        });
      }
    } catch (err) {
      console.error(err);
      throw new Error('error');
    }
  };

  // 사용자 chip 제거
  const removeChip = (item: string, idx: number) => {
    // 배열에서 특정 요소 제거
    const filteredArray = tableState[idx].userId.filter((el: string) => el !== item);

    setTableState((prev: ISchedule[]) => prev.map((el: ISchedule, i: number) => (i === idx ? { ...el, userId: filteredArray } : el)));
  };

  return (
    <>
      <Root>
        <InputTable className='margin-left-05 margin-left-05 overflow-y-scroll'>
          <div className='thead'>
            <div className='tr'>
              {/* <div className='trCol2p5 flex-center tableStickyNo'>No</div> */}
              <div className='trCol4 flex-center'>{t('코드')}</div>
              <div className='trCol10 flex-center required tableStickyTitle'>{t('항목명')}</div>
              <div className='trCol6 flex-center required'>{t('바탕색')}</div>
              <div className='trCol6 flex-center required'>{t('글자색')}</div>
              <div className='trCol6 flex-center'>{t('사용유무')}</div>
              <div className='trCol22 flex-center required content-overflow'>{t('사용자')}</div>
              {auth.deleteAuth && <div className='trCol6 flex-center'> </div>}
            </div>
          </div>
          <div className='table' ref={scrollContainerRef}>
            <div className='tbody'>
              {tableState?.map((el: ISchedule, index: number) => (
                <div className='tr' role='button' tabIndex={0} key={el.id}>
                  {/* <div className='trCol2p5 flex-center'>{index + 1}</div> */}
                  <div className='trCol4 flex-center'>{el.csCd}</div>
                  <div className='trCol10 flex-center'>
                    <input
                      type='text'
                      name='csName'
                      onChange={(e) => onChangeInputValue(e, index)}
                      className='colorButton'
                      style={{ backgroundColor: el.backColor, color: el.fontColor, ...(isSaveClicked ? applyBorderStyle(el.csName, 'red', 'csName') : undefined) }}
                      value={el.csName}
                      onClick={(event) => {
                        if (size.innerSize.W <= 1024) {
                          event.stopPropagation();
                        }
                      }}
                      maxLength={25}
                    />
                  </div>
                  <div className='trCol6 flex-center'>
                    <button
                      type='button'
                      onClick={() => onClickOpenColorPicker(el.backColor, index, 'bg')}
                      className='colorButton'
                      style={
                        el.backColor !== ''
                          ? {
                              backgroundColor: el.backColor,
                              border: el.backColor === '#ffffff' ? '1px solid #d5d5d5' : 'none',
                              ...(isSaveClicked ? applyBorderStyle(el.backColor, 'red', 'backColor') : undefined),
                            }
                          : { backgroundColor: 'transparent', border: '1px dashed #d5d5d5', ...(isSaveClicked ? applyBorderStyle(el.backColor, 'red', 'backColor') : undefined) }
                      }
                    >
                      {el.backColor === '' && <span className='colorSelect'>선택</span>}
                    </button>
                  </div>
                  <div className='trCol6 flex-center'>
                    <button
                      type='button'
                      onClick={() => onClickOpenColorPicker(el.fontColor, index, 'font')}
                      className='colorButton'
                      style={
                        el.fontColor !== ''
                          ? {
                              backgroundColor: el.fontColor,
                              border: el.fontColor === '#ffffff' ? '1px solid #d5d5d5' : 'none',
                              ...(isSaveClicked ? applyBorderStyle(el.fontColor, 'red', 'fontColor') : undefined),
                            }
                          : { backgroundColor: 'transparent', border: '1px dashed #d5d5d5', ...(isSaveClicked ? applyBorderStyle(el.fontColor, 'red', 'fontColor') : undefined) }
                      }
                    >
                      {el.fontColor === '' && <span className='colorSelect'>선택</span>}
                    </button>
                  </div>
                  <div className='trCol6 flex-center'>
                    <ClickableContainer button={<TableLamp use={el.useYn === 'Y'} />} onClick={() => onClickUseYn(el.useYn, index)} />
                  </div>
                  <div className='trCol22 flex-center content-overflow scroll jobtype-chip-container'>
                    <div //
                      className='button add-button'
                      style={isSaveClicked ? applyBorderStyle(el.userId[0], 'red', 'userId') : undefined}
                      onClick={() => {
                        // setJobtypeIdx(el.users);
                        setOpenModal({ status: true, type: 'chipType', state: tableState, el: tableState[index], index });
                      }}
                      role='button'
                      tabIndex={0}
                    >
                      <span className='material-symbols-rounded'>add</span>
                    </div>
                    {/* {el.userId[0] !== null && el.userId[0] !== '' ? ( */}
                    {el.userId.length > 0 ? (
                      <div className='scroll-container' style={{ display: 'flex', gap: '0.5rem' }}>
                        {el.userId.map((selectedItem: string) => (
                          <JobtypeChip //
                            key={selectedItem}
                            selectedItem={selectedItem}
                            removeJobtype={() => removeChip(selectedItem, index)}
                            name={findJobTypeName(selectedItem, flattenList) ?? selectedItem}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className='checklist-container'>
                        <div className='checklist'>
                          <span className='material-symbols-rounded'>check</span>
                          <span className='description'>{t('다중선택 가능')}</span>
                        </div>
                      </div>
                    )}
                    {el.userId.length > 0 && (
                      <div
                        className='button default-button'
                        onClick={() => setTableState((prev: ISchedule[]) => prev.map((item: ISchedule, i: number) => (i === index ? { ...item, userId: [] } : item)))}
                        role='button'
                        tabIndex={0}
                      >
                        <span className='material-symbols-rounded'>backspace</span>
                      </div>
                    )}
                    {el.csCd !== '' &&
                      scheduleApi.data.data[index]?.userId !== '' &&
                      scheduleApi.data.data[index]?.userId !== null &&
                      tableState[index]?.userId.join(',') !== (scheduleApi.data.data[index]?.userId.join(',') || '') && (
                        <div
                          className='button default-button'
                          onClick={() =>
                            setTableState((prev: ISchedule[]) => prev.map((item: ISchedule, i: number) => (i === index ? { ...item, userId: scheduleApi.data.data[index]?.userId } : item)))
                          }
                          role='button'
                          tabIndex={0}
                        >
                          <span className='material-symbols-rounded'>undo</span>
                        </div>
                      )}
                  </div>
                  {auth.deleteAuth && (
                    <div className='trCol6 flex-center'>
                      <BtnRed onClick={() => onClickDelete(el, index)}>{el.csCd !== '' ? t('삭제') : t('제거')}</BtnRed>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </InputTable>
        {(auth.createAuth || auth.updateAuth) && (
          <ButtonsWrapper style={{ justifyContent: 'flex-end' }}>
            {auth.createAuth && <BtnBlue onClick={onClickAddTableRow}>{t('신규항목 추가')}</BtnBlue>}
            {(auth.createAuth || auth.updateAuth) && <BtnBlue onClick={onClickSave}>{t('저장')}</BtnBlue>}
          </ButtonsWrapper>
        )}
      </Root>
      <Portal openModal={openModal?.status}>
        {openModal && openModal.type === 'delete' && <DeleteModal openModal={openModal} setOpenModal={setOpenModal} />}
        {openModal && openModal.title === 'colorPicker' && <ColorPickerModal openModal={openModal} setOpenModal={setOpenModal} />}
        {openModal && openModal.type === 'chipType' && <GroupChkModal openModal={openModal} setOpenModal={setOpenModal} type='array' title={t('사용자 선택')} comparisonList={userPartList} />}
      </Portal>
    </>
  );
};

export default CalendarItem;
